import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LayoutGrid, LayoutList, Pencil, Trash2 } from "lucide-react";
import {
  api,
  isInFlight,
  type Board,
  type BoardDetail,
  type BoardSummary,
  type Catalog,
  type Generation,
  type ImageAsset,
} from "../api";
import {
  AsyncButton,
  ConfirmModal,
  EmptyState,
  ErrorNote,
  IconButton,
  Modal,
  Skeleton,
  describeError,
} from "../components/ui";
import { BoardRail } from "../components/BoardRail";
import { Lightbox } from "../components/Lightbox";
import { Composer, emptyDraft, type ComposerDraft } from "../components/Composer";
import { FrameCard, FrameTiles, type FrameHandlers } from "../components/FrameCard";
import { Inspector } from "../components/Inspector";
import { useGenerationPolling } from "../hooks/useGenerationPolling";
import { usePreferences } from "../hooks/usePreferences";
import { useTileNavigation } from "../hooks/useTileNavigation";

/** One union, so two dialogs can never be open at once and no state can describe a dialog twice. */
type Dialog =
  | { kind: "delete-board"; board: Board }
  | { kind: "rename-board"; board: Board }
  | { kind: "zoom"; image: ImageAsset }
  | null;

interface StudioProps {
  catalog: Catalog | null;
  /** Phone-only: the rail is a slide-over there, and the masthead owns the button (§21.1). */
  railOpen: boolean;
  onCloseRail: () => void;
}

/**
 * Studio: boards on the left, the board's stream in the middle, the composer under it, and an
 * inspector that opens on a selected frame (§11.1).
 */
export function Studio({ catalog, railOpen, onCloseRail }: StudioProps): ReactElement {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [preferences, setPreferences] = usePreferences();

  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [detail, setDetail] = useState<BoardDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<{ title: string; detail?: string } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [draft, setDraft] = useState<ComposerDraft>(emptyDraft);
  const [recycled, setRecycled] = useState<Generation | null>(null);
  const streamRef = useRef<HTMLDivElement>(null);

  const refreshBoards = useCallback(async () => {
    try {
      const page = await api.listBoards();
      setBoards(page.items);
    } catch {
      // A stale rail is a much smaller problem than an error banner over the work.
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listBoards(undefined, controller.signal)
      .then((page) => setBoards(page.items))
      .catch((failure: unknown) => {
        if (!controller.signal.aborted) setError(describeError(failure));
      })
      .finally(() => {
        if (!controller.signal.aborted) setBoardsLoading(false);
      });
    return () => controller.abort();
  }, []);

  // No board in the URL means "carry on where you left off": the board this browser was last on,
  // if it is still there, and otherwise the most recently touched one (§20).
  const remembered = preferences.last_board_id;
  useEffect(() => {
    if (boardId || boardsLoading || boards.length === 0) return;
    const restored =
      remembered !== null && boards.some((board) => board.id === remembered)
        ? remembered
        : boards[0].id;
    navigate(`/b/${restored}`, { replace: true });
  }, [boardId, boardsLoading, boards, remembered, navigate]);

  useEffect(() => {
    if (boardId) setPreferences({ last_board_id: boardId });
  }, [boardId, setPreferences]);

  // The one loader for the open board. Every mutation below ends by calling it (§11.3).
  const load = useCallback(async () => {
    if (!boardId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      setDetail(await api.getBoard(boardId));
      setError(null);
    } catch (failure) {
      setDetail(null);
      setError(describeError(failure));
    } finally {
      setDetailLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A different board is a different piece of work: the draft and the inspector do not follow.
  useEffect(() => {
    setDraft(emptyDraft);
    setRecycled(null);
    setSelectedId(null);
  }, [boardId]);

  const generations = useMemo(() => detail?.generations ?? [], [detail]);

  const sequenceById = useMemo(() => {
    const map = new Map<string, number>();
    for (const generation of generations) map.set(generation.id, generation.sequence);
    return map;
  }, [generations]);

  const applyPoll = useCallback(
    (updated: Generation[]) => {
      setDetail((previous) => {
        if (!previous) return previous;
        const byId = new Map(updated.map((generation) => [generation.id, generation]));
        return {
          ...previous,
          generations: previous.generations.map(
            (generation) => byId.get(generation.id) ?? generation,
          ),
        };
      });
      // A frame that has just landed changes the board's cover and its position in the rail.
      if (updated.some((generation) => !isInFlight(generation.status))) void refreshBoards();
    },
    [refreshBoards],
  );

  useGenerationPolling(generations, applyPoll);

  // The newest frame is the one you are waiting for, so it should be the one you are looking at.
  useEffect(() => {
    const stream = streamRef.current;
    if (stream) stream.scrollTop = stream.scrollHeight;
  }, [generations.length]);

  const selected = selectedId
    ? (generations.find((generation) => generation.id === selectedId) ?? null)
    : null;

  // ── Actions ────────────────────────────────────────────────────────────────────────────

  const createBoard = useCallback(async () => {
    try {
      const board = await api.createBoard();
      await refreshBoards();
      navigate(`/b/${board.id}`);
    } catch (failure) {
      setError(describeError(failure));
    }
  }, [navigate, refreshBoards]);

  /** Pull an output back in as the next input. The lineage is recorded with it (§11.1). */
  const useAsInput = useCallback((image: ImageAsset) => {
    setDraft((current) =>
      current.attachments.some((attached) => attached.id === image.id)
        ? current
        : {
            ...current,
            attachments: [...current.attachments, image],
            parentGenerationId: current.parentGenerationId ?? image.generation_id,
          },
    );
  }, []);

  /** Branching is the deliberate version: start a fresh draft from exactly this frame. */
  const branchFrom = useCallback((generation: Generation) => {
    const image = generation.outputs[0];
    if (!image) return;
    setDraft({ attachments: [image], parentGenerationId: generation.id });
  }, []);

  /**
   * Repeat a frame's request: the references go into the draft as the assets already in the
   * library (no re-upload), and the composer takes the model, parameters and prompt.
   */
  const recycle = useCallback((generation: Generation) => {
    const withImage = (role: "image" | "mask") =>
      generation.inputs
        .filter((input) => input.role === role)
        .sort((a, b) => a.ordinal - b.ordinal)
        .flatMap((input) => (input.image ? [input.image] : []));

    const mask = withImage("mask")[0];
    setDraft({
      attachments: mask ? [...withImage("image"), mask] : withImage("image"),
      maskImageId: mask?.id,
      parentGenerationId: generation.parent_generation_id,
    });
    setRecycled(generation);
  }, []);

  const deleteGeneration = useCallback(
    async (generation: Generation) => {
      try {
        await api.deleteGeneration(generation.id);
        if (selectedId === generation.id) setSelectedId(null);
        await load();
        await refreshBoards();
      } catch (failure) {
        setError(describeError(failure));
      }
    },
    [load, refreshBoards, selectedId],
  );

  const clearRecycled = useCallback(() => setRecycled(null), []);

  const board = detail?.board;

  // One set of handlers for both layouts; the card and the tile do the same things to a frame.
  const frameHandlers: FrameHandlers = {
    onSelect: (chosen) => setSelectedId((current) => (current === chosen.id ? null : chosen.id)),
    onZoom: (image) => setDialog({ kind: "zoom", image }),
    onUseAsInput: useAsInput,
    onBranch: branchFrom,
    onRecycle: recycle,
    onDownload: downloadImage,
    onDelete: (chosen) => void deleteGeneration(chosen),
    onRetried: () => void load(),
  };

  // Arrow keys walk the tiles. Off while a dialog is up: the viewer and the sheets own the keys.
  useTileNavigation({
    enabled: preferences.board_view === "tiles" && dialog === null,
    containerRef: streamRef,
    orderedIds: generations.map((generation) => generation.id),
    selectedId,
    onSelect: setSelectedId,
  });

  // ── Render ─────────────────────────────────────────────────────────────────────────────

  const noBoards = !boardsLoading && boards.length === 0;

  return (
    <div className="app-body">
      <BoardRail
        boards={boards}
        loading={boardsLoading}
        open={railOpen}
        onClose={onCloseRail}
        onCreate={() => void createBoard()}
      />

      <div className="workspace">
        <div className="workspace-header">
          <h1 className="page-title truncate">{board?.title ?? "Studio"}</h1>
          {board && (
            <span className="stamp">
              {generations.length} {generations.length === 1 ? "frame" : "frames"}
            </span>
          )}
          <span className="grow" />
          {board && (
            <div className="row">
              {/* Stream or tiles. Tiles drop the prompt, ordinals and readings so more fit. */}
              <div className="segmented is-icons" role="group" aria-label="Board view">
                <button
                  type="button"
                  className={preferences.board_view === "stream" ? "is-selected" : undefined}
                  aria-pressed={preferences.board_view === "stream"}
                  aria-label="Stream"
                  title="Stream"
                  onClick={() => setPreferences({ board_view: "stream" })}
                >
                  <LayoutList size={15} />
                </button>
                <button
                  type="button"
                  className={preferences.board_view === "tiles" ? "is-selected" : undefined}
                  aria-pressed={preferences.board_view === "tiles"}
                  aria-label="Tiles"
                  title="Tiles"
                  onClick={() => setPreferences({ board_view: "tiles" })}
                >
                  <LayoutGrid size={15} />
                </button>
              </div>
              <IconButton
                onClick={() => setDialog({ kind: "rename-board", board })}
                label="Rename board"
              >
                <Pencil size={15} />
              </IconButton>
              <IconButton
                onClick={() => setDialog({ kind: "delete-board", board })}
                label="Delete board"
                className="btn ghost icon danger"
              >
                <Trash2 size={15} />
              </IconButton>
            </div>
          )}
        </div>

        <div className="stream" ref={streamRef}>
          {error && <ErrorNote title={error.title} detail={error.detail} />}

          {noBoards && (
            <EmptyState
              title="No boards yet."
              hint="A board is one thread of work: generate a frame, edit it, branch from it."
              action={
                <AsyncButton className="btn primary" onClick={createBoard}>
                  Create the first board
                </AsyncButton>
              }
            />
          )}

          {detailLoading && detail === null && (
            <>
              <Skeleton height={220} />
              <Skeleton height={220} />
            </>
          )}

          {detail && generations.length === 0 && (
            <EmptyState
              title="This board is empty."
              hint="Describe the first frame below, or attach a reference image to edit instead."
            />
          )}

          {preferences.board_view === "tiles" ? (
            <div className="tiles">
              {generations.map((generation) => (
                <FrameTiles
                  key={generation.id}
                  generation={generation}
                  selected={selectedId === generation.id}
                  {...frameHandlers}
                />
              ))}
            </div>
          ) : (
            generations.map((generation) => (
              <FrameCard
                key={generation.id}
                generation={generation}
                parentSequence={
                  generation.parent_generation_id
                    ? sequenceById.get(generation.parent_generation_id)
                    : undefined
                }
                selected={selectedId === generation.id}
                {...frameHandlers}
              />
            ))
          )}
        </div>

        {board &&
          (catalog ? (
            <Composer
              // Keyed by board: a half-written prompt belongs to the board it was written in.
              key={board.id}
              catalog={catalog}
              boardId={board.id}
              draft={draft}
              onDraftChange={setDraft}
              recycled={recycled}
              onRecycled={clearRecycled}
              onSubmitted={() => {
                setDraft(emptyDraft);
                void load();
                void refreshBoards();
              }}
            />
          ) : (
            <div className="composer">
              <Skeleton height={62} />
              <span className="stamp">Loading models…</span>
            </div>
          ))}
      </div>

      {selected && (
        <Inspector
          generation={selected}
          parentSequence={
            selected.parent_generation_id
              ? sequenceById.get(selected.parent_generation_id)
              : undefined
          }
          onClose={() => setSelectedId(null)}
          onUseAsInput={useAsInput}
          onDownload={downloadImage}
          onDelete={(generation) => void deleteGeneration(generation)}
        />
      )}

      {dialog?.kind === "rename-board" && (
        <RenameBoardDialog
          board={dialog.board}
          onCancel={() => setDialog(null)}
          onSave={async (title) => {
            try {
              await api.renameBoard(dialog.board.id, title);
              setDialog(null);
              await load();
              await refreshBoards();
            } catch (failure) {
              setError(describeError(failure));
              setDialog(null);
            }
          }}
        />
      )}

      {dialog?.kind === "delete-board" && (
        <ConfirmModal
          title="Delete board"
          danger
          confirmLabel="Delete board"
          body={
            <>
              <p>
                Deleting <strong>{dialog.board.title}</strong> removes every frame in it. The images
                themselves stay in your library.
              </p>
              <p className="field-help">This cannot be undone.</p>
            </>
          }
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            try {
              await api.deleteBoard(dialog.board.id, slugForTitle(dialog.board.title));
              setDialog(null);
              const page = await api.listBoards();
              setBoards(page.items);
              const next = page.items.length > 0 ? page.items[0] : null;
              // The board that was being remembered has just gone; forget it in the same breath.
              if (!next) setPreferences({ last_board_id: null });
              navigate(next ? `/b/${next.id}` : "/", { replace: true });
            } catch (failure) {
              setError(describeError(failure));
              setDialog(null);
            }
          }}
        />
      )}

      {dialog?.kind === "zoom" && (
        <Lightbox image={dialog.image} onDownload={downloadImage} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}

function RenameBoardDialog({
  board,
  onCancel,
  onSave,
}: {
  board: Board;
  onCancel: () => void;
  onSave: (title: string) => Promise<void>;
}): ReactElement {
  const [title, setTitle] = useState(board.title);
  const trimmed = title.trim();

  return (
    <Modal
      title="Rename board"
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
          <AsyncButton
            className="btn primary"
            onClick={() => onSave(trimmed)}
            disabled={trimmed === "" || trimmed === board.title}
            disabledReason={trimmed === "" ? "A board needs a title." : "That is already the title."}
          >
            Save
          </AsyncButton>
        </>
      }
    >
      <label className="field">
        <span className="field-label">Title</span>
        <input
          className="input"
          value={title}
          maxLength={200}
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>
    </Modal>
  );
}

/**
 * Mirrors Slugs.ForTitle in Darkroom.Api/Endpoints/EndpointSupport.cs. Destructive endpoints make
 * the caller echo the slug, so a stray DELETE cannot remove a board — which only works if the two
 * sides agree on the spelling.
 */
function slugForTitle(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^\p{L}\p{Nd}]/gu, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug === "" ? "board" : slug;
}

/** Same-origin content URLs, so the download attribute is honoured and no blob copy is needed. */
function downloadImage(image: ImageAsset): void {
  const link = document.createElement("a");
  link.href = image.content_url;
  link.download = `${image.id}.${image.content_type.split("/")[1] ?? "png"}`;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}
